// ─── How it works — Luồng dữ liệu + Rủi ro + Telegram + Hướng dẫn ───

import { SectionTitle, Card, Pill, ListItem } from './shared';

export function DataFlowSection() {
  const steps = [
    { icon: 'ri-router-line', color: 'text-accent-400', title: '1. Kết nối cTrader', desc: 'Admin kết nối tài khoản cTrader qua OAuth 2.0. Token được lưu trong ctrader_gateway_config và tự động refresh khi hết hạn.' },
    { icon: 'ri-broadcast-line', color: 'text-primary-400', title: '2. WebSocket → SSE', desc: 'Edge function ctrader-gateway mở WebSocket tới cTrader, nhận sự kiện spot (giá) và trendbar (nến), rồi truyền về trình duyệt dưới dạng SSE stream.' },
    { icon: 'ri-share-forward-line', color: 'text-secondary-400', title: '3. Chia sẻ stream', desc: 'shared-ctrader-stream gom nhiều consumer lại thành một kết nối SSE duy nhất cho mỗi tài khoản, tiết kiệm tài nguyên và tự reconnect khi mất kết nối.' },
    { icon: 'ri-braces-line', color: 'text-emerald-400', title: '4. Xử lý trong engine', desc: 'Từng engine nhận sự kiện, cập nhật buffer nến, tính chỉ báo và phát hiện tín hiệu. Spot tick được đẩy qua price bus để các engine khác dùng chung.' },
    { icon: 'ri-notification-3-line', color: 'text-accent-400', title: '5. Phản hồi', desc: 'Tín hiệu được hiển thị ngay trên giao diện và gửi qua Telegram. Lệnh đặt/đóng/sửa SLTP được gửi ngược lại cTrader qua gateway.' },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="data-flow"
        icon="ri-flow-chart"
        iconBg="bg-secondary-500/10"
        iconColor="text-secondary-400"
        kicker="Luồng dữ liệu"
        title="Từ cTrader đến tín hiệu"
        subtitle="Dữ liệu giá di chuyển theo một chuỗi liên tục, được tối ưu để vừa chạy realtime vừa không tốn quota."
      />

      <div className="space-y-3">
        {steps.map((s) => (
          <Card key={s.title} className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
              <i className={`${s.icon} ${s.color} text-lg w-5 h-5 flex items-center justify-center`}></i>
            </div>
            <div>
              <p className="text-foreground-900 text-sm font-semibold">{s.title}</p>
              <p className="text-foreground-600 text-xs mt-1 leading-relaxed">{s.desc}</p>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <p className="text-xs font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-loop-left-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
          Độ tin cậy của stream
        </p>
        <div className="flex flex-wrap gap-2">
          <Pill tone="primary"><i className="ri-check-line text-[10px]"></i>Tự reconnect (500ms thường, 2-15s khi lỗi)</Pill>
          <Pill tone="accent"><i className="ri-check-line text-[10px]"></i>Watchdog: reconnect nếu im lặng 45 giây</Pill>
          <Pill tone="secondary"><i className="ri-check-line text-[10px]"></i>Polling fallback khi stream chết</Pill>
        </div>
      </Card>
    </section>
  );
}

export function RiskSection() {
  const rules = [
    { icon: 'ri-bar-chart-2-line', color: 'text-accent-400', name: 'Rule 1 — Spread', desc: 'Chênh lệch giữa ask và bid phải ≤ 3 pips. Nếu spread quá cao, lệnh bị chặn để tránh trượt giá.' },
    { icon: 'ri-stack-line', color: 'text-primary-400', name: 'Rule 2 — Max Orders', desc: 'Tổng lệnh đang mở trên một symbol không được vượt maxOrders đã cấu hình. Khi chạm trần, toàn bộ engine tự tắt.' },
    { icon: 'ri-git-commit-line', color: 'text-emerald-400', name: 'Rule 3 — Single Symbol', desc: 'Chỉ một symbol được phép có lệnh mở tại một thời điểm. Các symbol khác bị chặn cho tới khi symbol đang giữ lệnh đóng hết.' },
    { icon: 'ri-moon-line', color: 'text-secondary-400', name: 'Rule 4 — After Hours', desc: 'Sau 23:00 GMT+7 (16:00 UTC) mỗi ngày, toàn bộ engine tự động tắt. Có thể bật/tắt rule này trong giao diện.' },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="risk"
        icon="ri-shield-check-line"
        iconBg="bg-primary-500/10"
        iconColor="text-primary-400"
        kicker="Quản lý rủi ro"
        title="Bốn quy tắc bảo vệ"
        subtitle="Trước khi bất kỳ lệnh nào được gửi đi, engine đều kiểm tra 4 quy tắc. Nếu vi phạm Rule 2, 3 hoặc 4, toàn bộ symbol sẽ tự reset về trạng thái OFF."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rules.map((r) => (
          <Card key={r.name} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
              <i className={`${r.icon} ${r.color} text-base w-5 h-5 flex items-center justify-center`}></i>
            </div>
            <div>
              <p className="text-foreground-900 text-sm font-semibold">{r.name}</p>
              <p className="text-foreground-600 text-xs mt-1 leading-relaxed">{r.desc}</p>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <p className="text-xs font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-lock-line text-secondary-400 w-4 h-4 flex items-center justify-center"></i>
          Chống đặt lệnh trùng (distributed lock)
        </p>
        <p className="text-foreground-600 text-xs leading-relaxed">
          Khi nhiều thiết bị cùng bấm BUY/SELL một lúc, mỗi lần đặt lệnh được bọc trong một
          <strong className="text-foreground-900"> khóa phân tán</strong> lưu trên Supabase. Trong khóa, engine
          kiểm tra lại số lệnh thật trên cTrader, nên hai máy không bao giờ đặt trùng lệnh vượt quá maxOrders.
        </p>
      </Card>
    </section>
  );
}

export function TelegramSection() {
  return (
    <section className="space-y-5">
      <SectionTitle
        id="telegram"
        icon="ri-telegram-line"
        iconBg="bg-accent-500/10"
        iconColor="text-accent-400"
        kicker="Thông báo"
        title="Tin nhắn Telegram"
        subtitle="Mọi cảnh báo đều được định dạng thống nhất và gắn thêm ngữ cảnh để bạn đọc là hiểu ngay."
      />

      <Card>
        <p className="text-foreground-600 text-xs mb-3">Cấu trúc một tin nhắn điển hình:</p>
        <pre className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-[11px] font-mono text-foreground-800 leading-relaxed overflow-x-auto whitespace-pre-wrap">
{`📊 [H1] <b>EURUSD</b> 🟢 BUY @ 1.0850
Strategy: Chin Bot — TMA50 Cross

Status: Level 1
Note: theo dõi vùng 1.0820 - 1.0880
Giá cắt lên trên TMA50 (chi tiết...)`}
        </pre>
      </Card>

      <div className="space-y-1">
        <ListItem icon="ri-price-tag-3-line" iconColor="text-accent-400" title="Status">
          Mỗi symbol có thể được gán trạng thái null / Level 1 / Level 2 từ giao diện monitor. Trạng thái này hiển thị trên mọi tin nhắn.
        </ListItem>
        <ListItem icon="ri-sticky-note-line" iconColor="text-primary-400" title="Note">
          Ghi chú tự do của từng symbol (mở popup note bằng nút "i"). Note chỉ xuất hiện khi symbol có nội dung, và được giữ nguyên khi bấm Reset.
        </ListItem>
        <ListItem icon="ri-toggle-line" iconColor="text-secondary-400" title="Mute từng loại">
          Bạn có thể tắt riêng cảnh báo TMA50, Tụ Tách, Box vs TMA50, hoặc từng symbol (Box). Engine vẫn chạy và ghi log, chỉ không gửi Telegram.
        </ListItem>
      </div>
    </section>
  );
}

export function GuideSection() {
  const guides = [
    {
      icon: 'ri-key-line',
      color: 'text-accent-400',
      title: '1. Kết nối tài khoản cTrader',
      steps: [
        'Đăng nhập bằng tài khoản Super Admin (admin@chin.vn).',
        'Vào Dashboard → cTrader Connection, kết nối qua OAuth.',
        'Chờ gateway trả về danh sách tài khoản (hiện ở badge trên Strategies).',
      ],
    },
    {
      icon: 'ri-brain-line',
      color: 'text-primary-400',
      title: '2. Tạo và kích hoạt strategy',
      steps: [
        'Vào Strategies → tạo strategy mới, chọn loại generic / trailingStop / chin.',
        'Với generic: thêm indicator + entry/exit rules + cấu hình risk.',
        'Với chin/trailingStop: lưu lại rồi mở màn monitor để theo dõi.',
      ],
    },
    {
      icon: 'ri-flashlight-line',
      color: 'text-accent-400',
      title: '3. Sử dụng Chin Bot Monitor',
      steps: [
        'Chọn một symbol list từ dropdown để bắt đầu stream dữ liệu.',
        'Gán Status (Level 1/2) và gõ Note cho từng symbol bằng nút "i".',
        'Cấu hình Play (Off/Order), volume, maxOrders rồi bấm Buy/Sell để vào lệnh.',
      ],
    },
    {
      icon: 'ri-send-plane-line',
      color: 'text-emerald-400',
      title: '4. Cấu hình Telegram',
      steps: [
        'Vào Dashboard → Telegram Bot Configuration, nhập Chat ID + Bot Token.',
        'Bấm "Test Telegram" trong monitor để kiểm tra kết nối.',
        'Tín hiệu sẽ tự động bắn khi engine phát hiện sự kiện.',
      ],
    },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="guide"
        icon="ri-map-pin-2-line"
        iconBg="bg-primary-500/10"
        iconColor="text-primary-400"
        kicker="Hướng dẫn"
        title="Bắt đầu từng bước"
        subtitle="Đây là quy trình gợi ý để đưa hệ thống vào vận hành từ con số không."
      />

      <div className="space-y-4">
        {guides.map((g) => (
          <Card key={g.title}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
                <i className={`${g.icon} ${g.color} text-base w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <p className="text-foreground-900 text-sm font-semibold">{g.title}</p>
            </div>
            <ol className="space-y-1.5">
              {g.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-foreground-400 text-xs font-mono mt-px">{i + 1}.</span>
                  <p className="text-foreground-700 text-xs leading-relaxed">{s}</p>
                </li>
              ))}
            </ol>
          </Card>
        ))}
      </div>
    </section>
  );
}